import * as XLSX from 'xlsx';

export function downloadIndividualReportAsExcel(filename, reportData, metadata) {
  try {
    const wb = XLSX.utils.book_new();

    // 1) Metadata sheet
    const pageLink = window.location.href;
    const metaRows = [
      ['Report ID', metadata?.report_id || ''],
      // Insert Employee row right after Report ID when present
      ...(function() {
        const employeeVal = metadata?.employee || metadata?.employee_name || metadata?.employee_id;
        return employeeVal ? [['Employee', employeeVal]] : [];
      })(),
      ['Date Range', (metadata?.report_start_time && metadata?.report_end_time)
        ? `${new Date(metadata.report_start_time * 1000).toLocaleDateString('en-GB')} - ${new Date(metadata.report_end_time * 1000).toLocaleDateString('en-GB')}`
        : ''
      ],
      ['Created At', metadata?.createdat ? new Date(metadata.createdat * 1000).toLocaleString('en-GB') : ''],
      ['Created By', metadata?.created_by || ''],
      ['Dashboard Link (You need dashboard access)', 'Open in Dashboard']
    ];
    const metaSheet = XLSX.utils.aoa_to_sheet(metaRows);
    // Convert the Link cell to a real hyperlink and add access note to the right
    try {
      const linkRowIdx = metaRows.findIndex(r => r && r[0] === 'Dashboard Link (You need dashboard access)');
      if (linkRowIdx !== -1) {
        const linkCellAddr = XLSX.utils.encode_cell({ c: 1, r: linkRowIdx }); // Column B
        metaSheet[linkCellAddr] = { t: 's', v: pageLink, l: { Target: pageLink, Tooltip: 'Open in browser' } };
        const noteCellAddr = XLSX.utils.encode_cell({ c: 2, r: linkRowIdx }); // Column C
        metaSheet[noteCellAddr] = { t: 's', v: '(You need dashboard access)' };
      }
    } catch (_) {}
    XLSX.utils.book_append_sheet(wb, metaSheet, 'Metadata');

    // 2) Analysis sheet
    // Columns: S. No., Metric, Score, Key Positive Points, Key Improvements
    const analysisRows = [['S. No.', 'Metric', 'Score', 'Key Positive Points', 'Key Improvements']];

    const joinPoints = (arr) => Array.isArray(arr) ? arr.map(p => (typeof p === 'string' ? p : p?.text || '')).filter(Boolean).join('\n') : '';

    // Pull agent_score for overall from metrics to use as fallback for skill_level score
    const getOverallAgentScoreFromMetrics = () => {
      const arr = Array.isArray(reportData?.metrics) ? reportData.metrics : [];
      const row = arr.find(r => String(r?.name || '').toLowerCase() === 'overall');
      const val = parseFloat(row?.agentScore);
      return isNaN(val) ? '' : val;
    };
    const overallAgentScore = getOverallAgentScoreFromMetrics();

    // Overall metrics (skill_level, previous_period_comparison)
    const pushOverall = (nameKey, label, fallbackScore = '') => {
      const section = reportData?.overall?.[nameKey];
      if (section) {
        analysisRows.push([
          analysisRows.length, // S. No.
          `overall:${label}`,
          section?.score ?? fallbackScore ?? '',
          joinPoints(section?.positive_points),
          joinPoints(section?.negative_points)
        ]);
      }
    };
    // Use fallback from metrics for skill_level score; remove agent_level row
    pushOverall('skill_level', 'skill_level', overallAgentScore);
    pushOverall('previous_period_comparison', 'previous_period_comparison');

    // Each parameter
    Object.entries(reportData?.parameters || {}).forEach(([paramName, paramData]) => {
      analysisRows.push([
        analysisRows.length,
        `parameter:${paramName}`,
        paramData?.ans ?? '',
        joinPoints(paramData?.positive_points),
        joinPoints(paramData?.negative_points)
      ]);
    });

    // Concerns and Queries (summary-level)
    // Score is computed as average of concerns_handling_pct / queries_handling_pct from metrics
    const metricsForAvg = Array.isArray(reportData?.metrics) ? reportData.metrics : [];
    const getWeightFromRow = (row) => {
      const candidates = ['calls'];
      for (let i = 0; i < candidates.length; i++) {
        const val = row?.[candidates[i]];
        const n = parseFloat(val);
        if (!isNaN(n) && n > 0) return n;
      }
      return 1;
    };
    const weightedAvgOfKey = (key) => {
      let num = 0;
      let den = 0;
      metricsForAvg.forEach((m) => {
        const v = parseFloat(m?.[key]);
        if (!isNaN(v)) {
          const w = getWeightFromRow(m);
          num += v * w;
          den += w;
        }
      });
      if (den <= 0) return '';
      return Math.round(num / den);
    };
    const concernsAvg = weightedAvgOfKey('concerns_handling_pct');
    const queriesAvg = weightedAvgOfKey('queries_handling_pct');

    if (reportData?.concerns_and_queries?.concerns) {
      const c = reportData.concerns_and_queries.concerns;
      analysisRows.push([
        analysisRows.length,
        'concerns',
        concernsAvg,
        joinPoints(c?.positive_points),
        joinPoints(c?.negative_points)
      ]);
    }
    if (reportData?.concerns_and_queries?.queries) {
      const q = reportData.concerns_and_queries.queries;
      analysisRows.push([
        analysisRows.length,
        'queries',
        queriesAvg,
        joinPoints(q?.positive_points),
        joinPoints(q?.negative_points)
      ]);
    }

    const analysisSheet = XLSX.utils.aoa_to_sheet(analysisRows);
    XLSX.utils.book_append_sheet(wb, analysisSheet, 'Analysis');

    // 3) Data Metrics sheet (table shown at end)
    // Expect an array of objects with consistent keys
    const metricsArray = Array.isArray(reportData?.metrics) ? reportData.metrics : [];
    if (metricsArray.length > 0) {
      const metricsSheet = XLSX.utils.json_to_sheet(metricsArray);
      XLSX.utils.book_append_sheet(wb, metricsSheet, 'Data Metrics');
    } else {
      const empty = XLSX.utils.aoa_to_sheet([['No metrics data']]);
      XLSX.utils.book_append_sheet(wb, empty, 'Data Metrics');
    }

    // 4) Concerns Data sheet
    const concernsRows = [['Category', 'Subcategory', 'Total', 'Handled', 'Handled %']];
    const concerns = reportData?.concerns_and_queries?.concerns?.concerns_categories || [];
    concerns.forEach((row) => {
      const total = parseInt(row?.total || 0, 10);
      const handled = parseInt(row?.handled || 0, 10);
      const pct = total > 0 ? Math.round((handled / total) * 100) : 0;
      concernsRows.push([row?.category || '', row?.subcategory || '', total, handled, pct]);
    });
    const concernsSheet = XLSX.utils.aoa_to_sheet(concernsRows);
    XLSX.utils.book_append_sheet(wb, concernsSheet, 'Concerns Data');

    // 5) Queries Data sheet
    const queriesRows = [['Category', 'Subcategory', 'Total', 'Handled', 'Handled %']];
    const queries = reportData?.concerns_and_queries?.queries?.queries_categories || [];
    queries.forEach((row) => {
      const total = parseInt(row?.total || 0, 10);
      const handled = parseInt(row?.handled || 0, 10);
      const pct = total > 0 ? Math.round((handled / total) * 100) : 0;
      queriesRows.push([row?.category || '', row?.subcategory || '', total, handled, pct]);
    });
    const queriesSheet = XLSX.utils.aoa_to_sheet(queriesRows);
    XLSX.utils.book_append_sheet(wb, queriesSheet, 'Queries Data');

    // Write file
    XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
  } catch (e) {
    console.error('Error generating Excel:', e);
  }
}

export function downloadTrainingSessionAsExcel(filename, trainingData, metadata) {
  try {
    const wb = XLSX.utils.book_new();

    // 1) Metadata sheet
    const pageLink = window.location.href;
    const metaRows = [
      ['Training Session ID', metadata?.training_session_id || ''],
      ['Employee ID', metadata?.employee_id || ''],
      ['Date Range', (metadata?.report_start_time && metadata?.report_end_time)
        ? `${new Date(metadata.report_start_time * 1000).toLocaleDateString('en-GB')} - ${new Date(metadata.report_end_time * 1000).toLocaleDateString('en-GB')}`
        : ''
      ],
      ['Created At', metadata?.createdat ? new Date(metadata.createdat * 1000).toLocaleString('en-GB') : ''],
      ['Due At', metadata?.due_at ? new Date(metadata.due_at * 1000).toLocaleString('en-GB') : ''],
      ['Total Modules', trainingData?.total_modules ?? ''],
      ['Completed Modules', trainingData?.completed_modules ?? ''],
      ['Total Questions', trainingData?.total_questions ?? ''],
      ['Completed Questions', trainingData?.completed_questions ?? ''],
      ['Score', trainingData?.score ?? ''],
      ['Dashboard Link (You need dashboard access)', 'Open in Dashboard']
    ];
    const metaSheet = XLSX.utils.aoa_to_sheet(metaRows);
    try {
      const linkRowIdx = metaRows.findIndex(r => r && r[0] === 'Dashboard Link (You need dashboard access)');
      if (linkRowIdx !== -1) {
        const linkCellAddr = XLSX.utils.encode_cell({ c: 1, r: linkRowIdx }); // Column B
        metaSheet[linkCellAddr] = { t: 's', v: pageLink, l: { Target: pageLink, Tooltip: 'Open in browser' } };
        const noteCellAddr = XLSX.utils.encode_cell({ c: 2, r: linkRowIdx }); // Column C
        metaSheet[noteCellAddr] = { t: 's', v: '(You need dashboard access)' };
      }
    } catch (_) {}
    XLSX.utils.book_append_sheet(wb, metaSheet, 'Metadata');

    // Helpers
    const joinPoints = (arr) => Array.isArray(arr)
      ? arr.map(p => {
          if (typeof p === 'string') return p;
          if (p && typeof p === 'object') return p.text || p.title || '';
          return '';
        }).filter(Boolean).join('\n')
      : '';
    const getScenarioQuestion = (sc) => {
      if (!sc) return '';
      if (typeof sc.question === 'string') return sc.question;
      if (Array.isArray(sc.question)) {
        // Try to pull text fields from an array of parts
        const text = sc.question
          .map(q => (typeof q === 'string' ? q : (q?.text || '')))
          .filter(Boolean)
          .join(' ');
        if (text) return text;
      }
      return '';
    };
    const getScenarioLine = (sc) => {
      const q = getScenarioQuestion(sc);
      const conv = Array.isArray(sc?.sources) && sc.sources.length > 0
        ? (sc.sources[0]?.conversation_id || '')
        : '';
      const sid = sc?.scenario_id != null ? sc.scenario_id : '';
      return `scenario_id: ${sid}${q ? ' | question: ' + q : ''}${conv ? ' | conversation_id: ' + conv : ''}`.trim();
    };
    const getConversationIdsForModule = (mod) => {
      const dedup = (arr) => Array.from(new Set(arr.map(v => String(v))));
      if (Array.isArray(mod?.training_scenarios) && mod.training_scenarios.length > 0) {
        const ids = mod.training_scenarios.flatMap(sc =>
          Array.isArray(sc?.sources) ? sc.sources.map(s => s?.conversation_id).filter(Boolean) : []
        );
        const uniq = dedup(ids);
        if (uniq.length > 0) return uniq.slice(0, 3);
      }
      const collectFromPoints = (points) => Array.isArray(points)
        ? points.flatMap(p => Array.isArray(p?.sources) ? p.sources.map(s => s?.conversation_id).filter(Boolean) : [])
        : [];
      const posIds = collectFromPoints(mod?.positive_points);
      const negIds = collectFromPoints(mod?.negative_points);
      return dedup([...posIds, ...negIds]).slice(0, 3);
    };

    // 2) Modules sheet (Analysis equivalent)
    const modules = Array.isArray(trainingData?.modules) ? trainingData.modules : [];
    const rows = [['S. No.', 'Metric', 'Key Positive Points', 'Key Improvements', 'scenario_id', 'Scenario_1', 'Scenario_2', 'Scenario_3', 'Module Score']];
    modules.forEach((mod, idx) => {
      const positive = joinPoints(mod?.positive_points);
      const negative = joinPoints(mod?.negative_points);
      const scenarios = Array.isArray(mod?.training_scenarios) ? mod.training_scenarios : [];
      const scenarioLines = scenarios.slice(0, 3).map(getScenarioLine).filter(Boolean).join('\n').split('\n').map(line => line.trim());
      const scenarioIdLines = getConversationIdsForModule(mod).join('\n');
      const moduleScore = mod?.score ?? '';
      rows.push([
        rows.length, // S. No.
        mod?.module_name || '',
        positive,
        negative,
        scenarioIdLines,
        scenarioLines[0],
        scenarioLines[1],
        scenarioLines[2],
        moduleScore
      ]);
    });

    const modSheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, modSheet, 'Training Modules');

    // Save workbook
    XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
  } catch (e) {
    console.error('Error generating Training Excel:', e);
  }
}


